var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, } from "react-hook-form";
import { FormHelperText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, IconButton, Button, FormControl, } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, DragIndicator, } from "@mui/icons-material";
import { buildFieldValidation } from "../../validators/field-components";
import { getDefaultCellValue, renderMatrixCell, } from "../../utils/MatrixCellRenderers";
import CommentArea from "../common/CommentArea";
import { CellType, MatrixConditionKeyEnum, } from "../../../../types/forms";
import { useElementConditions, useFormLogic, } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getMatrixColumnsConditionStatus, getMatrixVisibleColumns, computeColumnTotal, formatColumnTotalValue, getCommentFieldName, resolveMatrixExpressionCell, } from "../../../../utils/forms-functions";
import { getElementValueKey, MATRIX_DYNAMIC_DEFAULT_MAX_ROWS, MATRIX_DYNAMIC_DEFAULT_ROWS, } from "../../../../utils/helpers";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
import ConfirmDialog from "../common/ConfirmDialog";
function normalizeMatrixDynamicValues(raw, visibleColumns, elementCellType, initialRowsCount) {
    var arr = Array.isArray(raw) ? raw : new Array(initialRowsCount).fill({});
    return arr.map(function (row) {
        var normalizedData = {};
        for (var _i = 0, visibleColumns_1 = visibleColumns; _i < visibleColumns_1.length; _i++) {
            var col = visibleColumns_1[_i];
            var columnCellType = col.cellType || elementCellType;
            var colName = col.name;
            if (Object.prototype.hasOwnProperty.call(row, colName)) {
                normalizedData[colName] = row[colName];
            }
            else {
                normalizedData[colName] = getDefaultCellValue(columnCellType);
            }
        }
        return normalizedData;
    });
}
var MatrixDynamicField = function (_a) {
    var element = _a.element, errors = _a.errors;
    var _b = useFormLogic(), formValues = _b.formValues, elementsTypeInfoByName = _b.elementsTypeInfoByName, globalConstraints = _b.globalConstraints;
    var _c = useFormContext(), unregister = _c.unregister, getValues = _c.getValues, setValue = _c.setValue;
    var _d = useState(null), confirmDelete = _d[0], setConfirmDelete = _d[1];
    var valueKey = getElementValueKey(element);
    var error = errors[valueKey];
    var _e = useElementConditions(element), isVisible = _e.isVisible, isEnabled = _e.isEnabled, isInteractive = _e.isInteractive, fieldRequired = _e.isRequired;
    var _f = useState(null), draggedIndex = _f[0], setDraggedIndex = _f[1];
    var _g = useState(null), dragOverIndex = _g[0], setDragOverIndex = _g[1];
    var initialRowsCount = element.rowCount || MATRIX_DYNAMIC_DEFAULT_ROWS;
    var elementMinRowCount = element.minRowCount || 0;
    var elementMaxRowCount = element.maxRowCount || MATRIX_DYNAMIC_DEFAULT_MAX_ROWS;
    var valueKeys = useMemo(function () {
        return element.showCommentArea
            ? [valueKey, getCommentFieldName(valueKey)]
            : [valueKey];
    }, [valueKey, element.showCommentArea]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    var columns = useMemo(function () {
        return (element.columns || []);
    }, [element.columns]);
    var visibleColumns = useMemo(function () {
        return getMatrixVisibleColumns(columns, formValues, elementsTypeInfoByName);
    }, [columns, formValues, elementsTypeInfoByName]);
    var columnsDisabledByEnableIf = useMemo(function () {
        return getMatrixColumnsConditionStatus({
            visibleColumns: visibleColumns,
            formValues: formValues,
            elementsTypeInfoByName: elementsTypeInfoByName,
            conditionKey: MatrixConditionKeyEnum.ENABLE_IF,
            shouldInvertLogic: true,
        });
    }, [visibleColumns, formValues, elementsTypeInfoByName]);
    var columnsRequiredByRequiredIf = useMemo(function () {
        return getMatrixColumnsConditionStatus({
            visibleColumns: visibleColumns,
            formValues: formValues,
            elementsTypeInfoByName: elementsTypeInfoByName,
            conditionKey: MatrixConditionKeyEnum.REQUIRED_IF,
        });
    }, [visibleColumns, formValues, elementsTypeInfoByName]);
    var validation = useMemo(function () {
        return buildFieldValidation(__assign(__assign({}, element), { columns: visibleColumns }), {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
            matrixColumnDisabledByEnableIf: columnsDisabledByEnableIf,
            matrixColumnRequiredByRequiredIf: columnsRequiredByRequiredIf,
        });
    }, [
        element,
        fieldRequired,
        isEnabled,
        visibleColumns,
        columnsDisabledByEnableIf,
        columnsRequiredByRequiredIf,
    ]);
    var choices = useMemo(function () {
        return (element.choices || []);
    }, [element.choices]);
    var choiceValues = useMemo(function () {
        return choices.map(function (choice) {
            if (typeof choice === "number") {
                return { value: choice.toString(), text: choice.toString() };
            }
            if (typeof choice === "string") {
                return { value: choice, text: choice };
            }
            return { value: choice.value.toString(), text: choice.text };
        });
    }, [choices]);
    var elementCellType = element.cellType || CellType.DEFAULT;
    var rateCount = element.rateCount || 5;
    useEffect(function () {
        if (!isVisible)
            return;
        var current = getValues(valueKey);
        var normalized = normalizeMatrixDynamicValues(current, visibleColumns, elementCellType, initialRowsCount);
        if (JSON.stringify(current !== null && current !== void 0 ? current : null) !== JSON.stringify(normalized)) {
            setValue(valueKey, normalized);
        }
    }, [
        isVisible,
        valueKey,
        visibleColumns,
        elementCellType,
        initialRowsCount,
        getValues,
        setValue,
    ]);
    var handleCellChange = function (rowIndex, column, value, currentValues, onChange) {
        var _a;
        var newValues = __spreadArray([], currentValues, true);
        if (!newValues[rowIndex]) {
            newValues[rowIndex] = {};
        }
        newValues[rowIndex] = __assign(__assign({}, newValues[rowIndex]), (_a = {}, _a[column] = value, _a));
        onChange(newValues);
    };
    var handleAddRow = function (cols, currentValues, onChange) {
        var newRow = {};
        cols.forEach(function (col) {
            var columnValue = col.name;
            var columnCellType = col.cellType || elementCellType;
            newRow[columnValue] = getDefaultCellValue(columnCellType);
        });
        onChange(__spreadArray(__spreadArray([], currentValues, true), [newRow], false));
    };
    var handleRemoveRow = function (rowIndex, currentValues, onChange) {
        var newValues = currentValues.filter(function (_, index) { return index !== rowIndex; });
        onChange(newValues);
    };
    var handleDragStart = function (e, index) {
        if (!isInteractive || !element.allowRowsDragAndDrop) {
            e.preventDefault();
            return;
        }
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/html", index.toString());
        setTimeout(function () {
            if (e.currentTarget instanceof HTMLElement) {
                e.currentTarget.style.opacity = "0.5";
            }
        }, 0);
    };
    var handleDragEnd = function (e) {
        if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = "1";
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
    };
    var handleDragOver = function (e, index) {
        if (!element.allowRowsDragAndDrop)
            return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };
    var handleDragLeave = function () {
        setDragOverIndex(null);
    };
    var handleDrop = function (e, dropIndex, currentValues, onChange) {
        if (!element.allowRowsDragAndDrop)
            return;
        e.preventDefault();
        setDragOverIndex(null);
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }
        var newValues = __spreadArray([], currentValues, true);
        var draggedItem = newValues[draggedIndex];
        newValues.splice(draggedIndex, 1);
        newValues.splice(dropIndex, 0, draggedItem);
        onChange(newValues);
        setDraggedIndex(null);
    };
    var addNewLine = function (element, currentValues, field) {
        return (element.allowAddRows === undefined &&
            currentValues.length < elementMaxRowCount && (_jsx(Box, __assign({ sx: {
                mt: 2,
                display: "flex",
                justifyContent: "flex-end",
            } }, { children: _jsx(Button, __assign({ variant: "outlined", startIcon: _jsx(AddIcon, {}), onClick: function () {
                    return handleAddRow(visibleColumns, currentValues, field.onChange);
                }, disabled: !isEnabled, size: "small" }, { children: element.addRowText || "Adicionar Linha" })) }))));
    };
    return (_jsxs(_Fragment, { children: [isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ fullWidth: true, error: !!error, sx: { mb: 3 } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: valueKey, rules: {
                                validate: function (value) {
                                    var _a;
                                    var result = validation.safeParse(value);
                                    if (!result.success) {
                                        return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                    }
                                    return true;
                                },
                            }, render: function (_a) {
                                var _b;
                                var field = _a.field;
                                var currentValues = ((_b = field.value) !== null && _b !== void 0 ? _b : []);
                                return (_jsxs(Box, { children: [["top", "topBottom"].includes(element === null || element === void 0 ? void 0 : element.addRowLocation) && (_jsx("div", __assign({ style: { marginBottom: "1rem" } }, { children: addNewLine(element, currentValues, field) }))), _jsx(TableContainer, __assign({ sx: {
                                                boxShadow: "none",
                                                backgroundColor: "transparent",
                                            } }, { children: _jsxs(Table, __assign({ size: "small" }, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                                        fontWeight: 600,
                                                                        width: 50,
                                                                    } }), visibleColumns.map(function (column) {
                                                                    var _a;
                                                                    var displayTitle = column.title || column.name;
                                                                    var columnIsRequired = column.isRequired ||
                                                                        columnsRequiredByRequiredIf[column.name];
                                                                    var requiredText = (_a = globalConstraints.requiredText) !== null && _a !== void 0 ? _a : "*";
                                                                    return (_jsxs(TableCell, __assign({ align: "center", sx: {
                                                                            fontWeight: 600,
                                                                            minWidth: 150,
                                                                        } }, { children: [displayTitle, " ", columnIsRequired && requiredText && (_jsx(Box, __assign({ component: "span", sx: { color: "error.main" } }, { children: requiredText })))] }), column.name));
                                                                })] }) }), _jsxs(TableBody, { children: [currentValues.map(function (row, rowIndex) { return (_jsxs(TableRow, __assign({ draggable: element.allowRowsDragAndDrop && !!isInteractive, onDragStart: function (e) { return handleDragStart(e, rowIndex); }, onDragOver: function (e) { return handleDragOver(e, rowIndex); }, onDragEnd: handleDragEnd, onDragLeave: handleDragLeave, onDrop: function (e) {
                                                                    return handleDrop(e, rowIndex, currentValues, field.onChange);
                                                                }, sx: {
                                                                    opacity: draggedIndex === rowIndex ? 0.5 : 1,
                                                                    bgcolor: dragOverIndex === rowIndex
                                                                        ? "action.selected"
                                                                        : draggedIndex === rowIndex
                                                                            ? "action.hover"
                                                                            : "transparent",
                                                                    cursor: element.allowRowsDragAndDrop &&
                                                                        !!isInteractive
                                                                        ? "grab"
                                                                        : "default",
                                                                    transition: "all 0.2s ease-in-out",
                                                                    "&:active": {
                                                                        cursor: element.allowRowsDragAndDrop &&
                                                                            !!isInteractive
                                                                            ? "grabbing"
                                                                            : "default",
                                                                    },
                                                                } }, { children: [_jsx(TableCell, { children: _jsxs(Box, __assign({ sx: { display: "flex", gap: 0.5 } }, { children: [element.allowRowsDragAndDrop && (_jsx(IconButton, __assign({ size: "small", disabled: !isInteractive, sx: {
                                                                                        cursor: !isInteractive
                                                                                            ? "default"
                                                                                            : "grab",
                                                                                        color: !isInteractive
                                                                                            ? "action.disabled"
                                                                                            : "action.active",
                                                                                    }, "aria-label": "Arrastar linha" }, { children: _jsx(DragIndicator, { fontSize: "small" }) }))), element.allowRemoveRows !== false &&
                                                                                    currentValues.length >
                                                                                        elementMinRowCount && (_jsx(_Fragment, { children: element.removeRowText ? (_jsx(Button, __assign({ size: "small", onClick: function () {
                                                                                            if (element === null || element === void 0 ? void 0 : element.confirmDelete) {
                                                                                                setConfirmDelete({
                                                                                                    rowIndex: rowIndex,
                                                                                                    currentValues: currentValues,
                                                                                                    onChange: field.onChange,
                                                                                                });
                                                                                            }
                                                                                            else {
                                                                                                handleRemoveRow(rowIndex, currentValues, field.onChange);
                                                                                            }
                                                                                        }, disabled: !isInteractive, color: "error", "aria-label": "Remover linha" }, { children: element.removeRowText }))) : (_jsx(IconButton, __assign({ size: "small", onClick: function () {
                                                                                            if (element === null || element === void 0 ? void 0 : element.confirmDelete) {
                                                                                                setConfirmDelete({
                                                                                                    rowIndex: rowIndex,
                                                                                                    currentValues: currentValues,
                                                                                                    onChange: field.onChange,
                                                                                                });
                                                                                            }
                                                                                            else {
                                                                                                handleRemoveRow(rowIndex, currentValues, field.onChange);
                                                                                            }
                                                                                        }, disabled: !isInteractive, color: "error", "aria-label": "Remover linha" }, { children: _jsx(DeleteIcon, { fontSize: "small" }) }))) }))] })) }), visibleColumns.map(function (column) {
                                                                        var _a;
                                                                        var columnValue = column.name;
                                                                        var columnCellType = column.cellType || elementCellType;
                                                                        var cellValue = columnCellType === CellType.EXPRESSION
                                                                            ? resolveMatrixExpressionCell(column, row !== null && row !== void 0 ? row : {}, formValues)
                                                                            : (_a = row[columnValue]) !== null && _a !== void 0 ? _a : getDefaultCellValue(columnCellType);
                                                                        var cellDisabled = element.readOnly === true ||
                                                                            !isInteractive ||
                                                                            column.readOnly === true ||
                                                                            Boolean(columnsDisabledByEnableIf[columnValue]);
                                                                        return (_jsx(TableCell, __assign({ align: "center" }, { children: renderMatrixCell({
                                                                                row: rowIndex,
                                                                                column: columnValue,
                                                                                cellValue: cellValue,
                                                                                element: element,
                                                                                cellDisabled: cellDisabled,
                                                                                cellType: columnCellType,
                                                                                columnDefinition: column,
                                                                                choiceValues: choiceValues,
                                                                                rateCount: rateCount,
                                                                                autoGrowComment: globalConstraints.autoGrowComment,
                                                                                onCellChange: function (row, column, value) {
                                                                                    return handleCellChange(row, column, value, currentValues, field.onChange);
                                                                                },
                                                                                error: error,
                                                                            }) }), columnValue));
                                                                    })] }), rowIndex)); }), visibleColumns.some(function (col) { return col.totalType !== undefined; }) && (_jsxs(TableRow, { children: [_jsx(TableCell, { sx: { fontWeight: 600 } }), visibleColumns.map(function (column) {
                                                                        if (!column.totalType) {
                                                                            return _jsx(TableCell, {}, column.name);
                                                                        }
                                                                        var colValues = currentValues.map(function (row) { return row[column.name]; });
                                                                        var total = computeColumnTotal(colValues, column.totalType);
                                                                        var formatted = formatColumnTotalValue(total, column.totalDisplayStyle, column.totalCurrency);
                                                                        return (_jsx(TableCell, __assign({ align: "right", sx: {
                                                                                fontWeight: 600,
                                                                                borderBottom: "1px solid #e0e0e0 !important",
                                                                            } }, { children: formatted }), column.name));
                                                                    })] }))] })] })) })), ["bottom", "topBottom", undefined].includes(element === null || element === void 0 ? void 0 : element.addRowLocation) && addNewLine(element, currentValues, field), error && (_jsx(FormHelperText, __assign({ error: true, sx: { mt: 1 } }, { children: error.message }))), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] }));
                            } })] })) }))) : null, _jsx(ConfirmDialog, { open: !!confirmDelete, title: "Remover linha", message: (element === null || element === void 0 ? void 0 : element.confirmDeleteText) || "Tem certeza de que deseja excluir?", confirmLabel: "Remover", confirmColor: "error", onCancel: function () { return setConfirmDelete(null); }, onConfirm: function () {
                    if (!confirmDelete)
                        return;
                    handleRemoveRow(confirmDelete.rowIndex, confirmDelete.currentValues, confirmDelete.onChange);
                    setConfirmDelete(null);
                } })] }));
};
export default MatrixDynamicField;
